// ---------------------------------------------------------------------------
// Comments Service - V11 Discovery API
// ---------------------------------------------------------------------------
// API client for V11 Discovery Comments endpoints
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'

export interface CommentsFilters {
  readonly page?: number
  readonly limit?: number
  readonly sort?: string
  readonly order?: 'asc' | 'desc'
  readonly search?: string
  readonly date_from?: string
  readonly date_to?: string
  readonly platform?: readonly number[]
  readonly emotion?: readonly number[]
  readonly language?: readonly number[]
  readonly sentiment_min?: number
  readonly sentiment_max?: number
  readonly toxic?: boolean
  readonly spam?: boolean
  /** Filter comments by post/content ID (for drill-down from Content) */
  readonly post_id?: string | number
  /** Include comments from the account's own channels (default: false) */
  readonly include_own_channels?: string
}

export interface CommentsResponse {
  readonly success: boolean
  readonly data: readonly Record<string, unknown>[]
  readonly meta: {
    readonly page: number
    readonly limit: number
    readonly total: number
    readonly lastPage: number
    readonly hasMorePages: boolean
  }
}

export interface CommentsAggregationsResponse {
  readonly success: boolean
  readonly data: {
    readonly platforms: readonly { name: string; count: number }[]
    readonly sentimentBuckets: readonly { name: string; count: number }[]
    readonly emotions: readonly { name: string; count: number }[]
    readonly languages: readonly { name: string; count: number }[]
    readonly toxic: readonly { name: string; count: number }[]
    readonly spam: readonly { name: string; count: number }[]
    readonly authors: readonly { name: string; count: number }[]
    readonly dates: readonly { name: string; count: number }[]
    readonly totals: {
      readonly comments: number
      readonly avgSentiment: number
      readonly toxicRate: number
      readonly spamRate: number
    }
  }
}

export interface CommentsTrendsResponse {
  readonly success: boolean
  readonly data: {
    readonly metric: string
    readonly groupBy: string
    readonly timeline: readonly Record<string, unknown>[]
  }
}

export const commentsService = {
  async getComments(filters: CommentsFilters = {}): Promise<CommentsResponse> {
    const response = await api.get<CommentsResponse>('/api/v11/discovery/comments', {
      params: filters,
    })
    return response
  },

  async getAggregations(filters: CommentsFilters = {}): Promise<CommentsAggregationsResponse> {
    const response = await api.get<CommentsAggregationsResponse>(
      '/api/v11/discovery/comments/aggregations',
      { params: filters }
    )
    return response
  },

  async getTrends(
    metric: string,
    groupBy: string,
    filters: CommentsFilters = {}
  ): Promise<CommentsTrendsResponse> {
    const response = await api.get<CommentsTrendsResponse>(
      '/api/v11/discovery/comments/trends',
      { params: { metric, group_by: groupBy, ...filters } }
    )
    return response
  },
}
